// IMPORT REACT
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// IMPORT PACKAGES
import * as Yup from "yup";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { format, isValid } from "date-fns";

// IMPORT CSS
import "../../../theme/style.scss";
import "react-phone-number-input/style.css";

// IMPORT UI COMPONENTS
import {
  AppBar,
  Box,
  Button,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  Toolbar,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  CustomAsyncSelect,
  CustomSelect,
} from "../../../components/CustomFields/CustomSelect";
import SkeletonWrapper from "../../../components/Skeleton/SkeletonWrapper";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { CustomTextField } from "../../../components/CustomFields/TextField";
import { ExpandLess, ExpandMore } from "@mui/icons-material";

// IMPORT SERVICE
import { AlertType } from "../../../services/models/AlertType";
import { EncryptDecryptService } from "../../../services/common/EncryptionService";
import {
  GetMedicalRepresentativeDetail,
  CreateMedicalRepresentative,
  UpdateMedicalRepresentative,
  GetMedicalRepresentativeNumber,
} from "../../../services/MedicalRepresentativeService";
import MedicineCompanySave from "../../../pages/Medicine/Medicinecompany/MedicinecompanySave";
import { GetCountryListForSelect } from "../../../services/CountryService";
import { GetCountryRegionListForSelect } from "../../../services/CountryRegionService";
import { PreferencesContext } from "../../../PreferenceContext";
import CustomBreadcrumbs from "../../../components/BreadcrumbsComponent/CustomBreadcrumbs";
import PhoneInput from "react-phone-number-input";
import DatePickerComponent from "../../../components/CustomFields/DatePicker";
import dayjs from "dayjs";
import { GetLanguageListForSelect } from "../../../services/LanguageService";
import { GetMedicineCompanyList } from "../../../services/MedicineCompanyService";
import CustomTooltip from "../../../components/ToolTip/CustomToolTip";

// SCHEMA FOR OPTION
interface selectOption {
  label: string;
  value: string;
}

// SCHEMA FOR FORM
interface formInitialValueTypes {
  medicalRepresentativeId: number;
  medicalRepresentativeNumber: string;
  medicalRepresentativeAutoNumber: number;
  prefix: selectOption | null;
  firstName: string;
  middleName: string;
  lastName: string;
  dateOfBirth: string;
  preferredLanguage: selectOption | null;
  maritalStatus: selectOption | null;
  gender: selectOption | null;
  medicineCompanyId: selectOption | null;
  address1: string;
  address2: string;
  city: string;
  postalCode: string;
  countryId: selectOption | null;
  countryName: string | null;
  countryRegionId: selectOption | null;
  countryRegionName: string | null;
  email: string;
  primaryMobileNumber: string;
  secondaryMobileNumber: string;
  isDeleted: boolean;
}

// DEFAULT SCHEMA VALUE FOR FORM

const prefixOptions: selectOption[] = [
  { value: "Dr.", label: "Dr." },
  { value: "Mr.", label: "Mr." },
  { value: "Ms.", label: "Ms." },
  { value: "Mrs.", label: "Mrs." },
  { value: "Prof.", label: "Prof." },
];

const genderOptions: selectOption[] = [
  { value: "MALE", label: "Male" },
  { value: "FEMALE", label: "Female" },
  { value: "OTHER", label: "Other" },
];

const maritalStatusOptions: selectOption[] = [
  { value: "SINGLE", label: "Single" },
  { value: "MARRIED", label: "Married" },
  { value: "DIVORCED", label: "Divorced" },
  { value: "WIDOWED", label: "Widowed" },
  { value: "REMARRIED", label: "Remarried" },
];

export default function MedicalRepresentativeSave(Props: any) {
  const { isForPopUp = false, handleClosePopUp, name } = Props;

  const initMedicalRepresentativeData: formInitialValueTypes = {
    medicalRepresentativeId: 0,
    medicalRepresentativeNumber: "",
    medicalRepresentativeAutoNumber: 0,
    prefix: null,
    firstName: isForPopUp ? name?.toUpperCase() : "",
    middleName: "",
    lastName: "",
    dateOfBirth: "",
    preferredLanguage: null,
    maritalStatus: null,
    gender: null,
    medicineCompanyId: null,
    address1: "",
    address2: "",
    city: "",
    postalCode: "",
    countryId: { label: "INDIA", value: "91" },
    countryRegionId: { label: "Gujarat (24)", value: "7" },
    email: "",
    primaryMobileNumber: "",
    secondaryMobileNumber: "",
    isDeleted: false,
    countryName: null,
    countryRegionName: null,
  };
  // HOOKS AND CONTEXT
  const navigate = useNavigate();
  const edService = new EncryptDecryptService();
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }

  // GETTTING PARAMS
  let urlParams = useQuery();
  let curRawMedicalRepresentativeId = urlParams.get("q");
  let curMedicalRepresentativeId = 0;
  if (
    curRawMedicalRepresentativeId !== null &&
    curRawMedicalRepresentativeId !== undefined
  ) {
    let decId = edService.decrypt(curRawMedicalRepresentativeId);
    if (decId !== "" && decId !== null && decId !== undefined) {
      curMedicalRepresentativeId = +decId;
    }
  } else {
    if (urlParams.size > 0) {
      navigate("/not-found");
    }
  }

  // STATES FOR DATA STORAGE  -- PLEASE USE PROPER TYPES FOR STATE
  const [countryRegionData, setCountryRegionData] = useState<any[]>([]); // REPLACE ANY WITH COUNTRY INTERFACE
  const [countryData, setCountryData] = useState<any[]>([]); // REPLACE ANY WITH STATE INTERFACE
  const [languageData, setLanguageData] = useState([]);
  const [medicineCompanyData, setMedicineCompanyData] = useState<any[]>([]);
  const [seeMore, setSeeMore] = useState<boolean>(false);

  const [customPopUp, setCustomPopUp] = React.useState({
    open: false,
    name: "",
  });

  const handleClickOpen = (value: boolean, label: string) => {
    setCustomPopUp({ ...customPopUp, open: value, name: label });
  };

  const handleClose = () => {
    setCustomPopUp({ ...customPopUp, open: false });
  };

  const handleManagePopup = async (code, company) => {
    setCustomPopUp({ ...customPopUp, open: false });
    if (code === "MEDICINE_COMPANY_CREATE") {
      methods.setValue(
        "medicineCompanyId",
        {
          label: company?.medicinCompanyLable,
          value: company?.medicineCompanyId?.toString(),
        },
        {
          shouldValidate: true,
        }
      );
      setIsLoading(true);
      await fetchMedicineCompanyName("", true);
      setIsLoading(false);
    }
  };

  // STATES -- FOR ERROR HANDLING AND LOADING
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showSkeleton, setShowSkeleton] = useState<boolean>(true);

  //SCHEMA VALIDATION
  const validationSchema = Yup.object().shape({
    medicalRepresentativeId: Yup.number().nullable(),
    medicalRepresentativeNumber: Yup.string().required(
      "Medical Representative Number is required"
    ),
    medicalRepresentativeAutoNumber: Yup.number().required(),
    prefix: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    firstName: Yup.string().required("First Name is required"),
    middleName: Yup.string().nullable(),
    lastName: Yup.string().required("LastName is required"),
    dateOfBirth: Yup.string().nullable(),
    preferredLanguage: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    maritalStatus: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    gender: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required("Gender is required"),
    medicineCompanyId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    address1: Yup.string().nullable(),
    address2: Yup.string().nullable(),
    city: Yup.string().nullable(),
    postalCode: Yup.string().nullable(),
    countryId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    countryName: Yup.string().nullable(),
    countryRegionName: Yup.string().nullable(),
    countryRegionId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    email: Yup.string().email("Invalid email").nullable(),
    primaryMobileNumber: Yup.string().nullable(),
    secondaryMobileNumber: Yup.string().nullable(),
    isDeleted: Yup.boolean(),
  });

  // FORM HOOK
  const methods = useForm({
    resolver: yupResolver(validationSchema), // IGNORE THIS ERROR
    defaultValues: initMedicalRepresentativeData, // DEFAULT VALUE -- DON'T CREATE STATE FOR IT -- STATE CAN INCREASE SITE LOAD
  });

  // GET MEDICAL REPRESENTATIVE DETAIL
  const getMedicalRepresentativeDetailById = async () => {
    let MedicalRepresentative: any;
    let params = new URLSearchParams();
    params.append(
      "medicalRepresentativeId",
      curMedicalRepresentativeId?.toString()
    );
    params.append("branchId", userContext?.branchId?.toString());
    params.append("orgId", userContext?.organizationId?.toString());
    params.append("consumerId", userContext?.consumerId?.toString());
    try {
      const res: any = await GetMedicalRepresentativeDetail(params);
      MedicalRepresentative = res.data.data;
      const genderconvertlabel = genderOptions.find(
        (x) => x.value === MedicalRepresentative.gender
      );
      const maritalStatusconvertlabel = maritalStatusOptions.find(
        (x) => x.value === MedicalRepresentative.maritalStatus
      );
      const prefixconvertlabel = prefixOptions.find(
        (x) => x.value === MedicalRepresentative.prefix
      );

      methods.reset({
        ...initMedicalRepresentativeData,
        medicalRepresentativeId:
          MedicalRepresentative?.medicalRepresentativeId || 0,
        medicalRepresentativeNumber:
          MedicalRepresentative?.medicalRepresentativeNumber || "",
        medicalRepresentativeAutoNumber:
          MedicalRepresentative?.medicalRepresentativeAutoNumber || "",
        prefix: prefixconvertlabel || null,
        firstName: MedicalRepresentative?.firstName || "",
        middleName: MedicalRepresentative?.middleName || "",
        lastName: MedicalRepresentative?.lastName || "",
        dateOfBirth: MedicalRepresentative?.dateOfBirth || null,
        preferredLanguage: MedicalRepresentative?.preferredLanguage
          ? {
              label: MedicalRepresentative?.preferredLanguageLable,
              value: MedicalRepresentative?.preferredLanguage,
            }
          : null,
        maritalStatus: maritalStatusconvertlabel || null,
        gender: genderconvertlabel || null,
        medicineCompanyId: MedicalRepresentative?.medicineCompanyId
          ? {
              label: MedicalRepresentative?.medicineCompanyNameLable,
              value: MedicalRepresentative?.medicineCompanyId?.toString(),
            }
          : null,
        address1: MedicalRepresentative?.address1 || "",
        address2: MedicalRepresentative?.address2 || "",
        city: MedicalRepresentative?.city || "",
        postalCode: MedicalRepresentative?.postalCode || "",
        countryId: MedicalRepresentative?.countryId
          ? {
              label: MedicalRepresentative?.countryName || "",
              value: MedicalRepresentative?.countryId?.toString(),
            }
          : null,
        countryName: MedicalRepresentative?.countryName || "",
        countryRegionId: MedicalRepresentative?.countryRegionId
          ? {
              label: MedicalRepresentative?.countryRegionName || "",
              value: MedicalRepresentative?.countryRegionId?.toString(),
            }
          : null,
        countryRegionName: MedicalRepresentative?.countryRegionName || "",
        email: MedicalRepresentative?.email || "",
        primaryMobileNumber: MedicalRepresentative?.primaryMobileNumber || "",
        secondaryMobileNumber:
          MedicalRepresentative?.secondaryMobileNumber || "",
      });
      return MedicalRepresentative;
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // SAVE AND UPDATE BRANCH
  const saveMedicalRepresentative = async (value: formInitialValueTypes) => {
    try {
      let result: any = "";
      let formData = {
        medicalRepresentativeId: value.medicalRepresentativeId,
        medicalRepresentativeNumber: value.medicalRepresentativeNumber,
        medicalRepresentativeAutoNumber: value.medicalRepresentativeAutoNumber,
        branchId: userContext?.branchId,
        orgId: userContext?.organizationId,
        consumerId: userContext?.consumerId,
        prefix: value.prefix?.value,
        firstName: value.firstName?.trim() || "",
        middleName: value.middleName?.trim() || "",
        lastName: value.lastName?.trim() || "",
        dateOfBirth: value.dateOfBirth?.trim() || null,
        preferredLanguage: value.preferredLanguage?.value,
        maritalStatus: value.maritalStatus?.value,
        gender: value.gender?.value,
        medicineCompanyId: value.medicineCompanyId?.value,
        address1: value.address1?.trim() || "",
        address2: value.address2?.trim() || "",
        city: value.city?.trim() || "",
        postalCode: value.postalCode?.trim() || "",
        countryRegionId: value.countryRegionId?.value,
        countryId: value.countryId?.value,
        countryName: value.countryName || "",
        countryRegionName: value.countryRegionName || "",
        email: value.email?.trim() || "",
        primaryMobileNumber: value.primaryMobileNumber?.trim() || "",
        secondaryMobileNumber: value.secondaryMobileNumber?.trim() || "",
        DataManagedBy: userContext.loginDetail.userId,
      };
      if (curMedicalRepresentativeId > 0) {
        result = await UpdateMedicalRepresentative(formData);
      } else {
        result = await CreateMedicalRepresentative(formData);
      }
      if (result?.data) {
        if (!isForPopUp) {
          navigate(-1);
        } else {
          handleClosePopUp("MR_CREATE");
        }
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result?.message,
          alertType: AlertType.Success,
        });
      } else {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: "Something went wrong.",
          alertType: AlertType.Error,
        });
      }
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // GET COUNTRIES
  async function fetchCountry() {
    try {
      let params = new URLSearchParams();
      const countryData: any = await GetCountryListForSelect(params);
      if (countryData?.data?.data) {
        let countyCollection = countryData.data.data;
        setCountryData(countyCollection);
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // FETCH STATE FOR COUNTRY
  async function fetchCountryRegionData(countryId) {
    try {
      let params = new URLSearchParams();
      params.append("countryId", countryId);
      const localCountryRegionData: any = await GetCountryRegionListForSelect(
        params
      );
      if (localCountryRegionData?.data?.data) {
        let stateCollection = localCountryRegionData.data.data;
        setCountryRegionData(stateCollection);
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // GET PREFERRED LANGUAGE
  const getLanguageList = async () => {
    try {
      const languageData: any = await GetLanguageListForSelect();
      if (languageData?.data?.data) {
        let languageCollection = languageData.data?.data;
        setLanguageData(languageCollection);
        setIsLoading(false);
      }
    } catch (e) {
      setIsLoading(false);
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // GET COMPANY NAME
  const fetchMedicineCompanyName = async (
    searchValue: string,
    oninit: boolean
  ) => {
    try {
      let medicineCompanyCollection = [];
      let params = new URLSearchParams();
      params.append("consumerId", userContext?.consumerId?.toString());
      params.append("orgId", userContext?.organizationId?.toString());
      params.append("branchId", userContext?.branchId?.toString());
      params.append("searchValue", searchValue);
      params.append("mode", "DROPDOWN".toString());
      const medicineCompanyData: any = await GetMedicineCompanyList(params);
      if (medicineCompanyData?.data?.data?.medicineCompanyColl) {
        medicineCompanyCollection =
          medicineCompanyData.data.data.medicineCompanyColl;
        if (oninit) setMedicineCompanyData(medicineCompanyCollection);
      }
      return medicineCompanyCollection;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // FILTER PATIENT
  async function filterMedicineCompany(inputValue: string) {
    let result = await fetchMedicineCompanyName(inputValue, false);
    return result;
  }

  // PATIENT LOAD ON SEARCH
  const loadMedicineCompanyList = (
    inputValue: string,
    callback: (options: any) => void
  ) => {
    if (inputValue && inputValue.length > 0) {
      filterMedicineCompany(inputValue?.trim()).then((r) => {
        callback(r);
      });
    } else {
      callback([]);
    }
  };

  // GET MEDICAL REPRESENTATIVE NUMBER
  const getMedicalRepresentativeNumber = async () => {
    try {
      let medicalRepresentativeNum;
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      const result: any = await GetMedicalRepresentativeNumber(params);
      if (result?.data?.data) {
        medicalRepresentativeNum = result.data.data;
        methods.reset({
          ...initMedicalRepresentativeData,
          medicalRepresentativeNumber:
            medicalRepresentativeNum?.prefix +
            medicalRepresentativeNum?.autoNumber,
          medicalRepresentativeAutoNumber:
            medicalRepresentativeNum?.autoNumber || 0,
        });
      }
      return medicalRepresentativeNum;
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // ON SUBMIT OF FORM
  const onSubmit = async (data: any) => {
    setIsLoading(true);
    await saveMedicalRepresentative(data);
    setIsLoading(false);
  };

  // HANDLE BACK
  const handleBack = () => {
    if (isForPopUp) {
      handleClosePopUp("CLOSE");
    } else {
      navigate(-1);
    }
  };

  // FETCH DATA
  const fetchData = async () => {
    try {
      await fetchMedicineCompanyName("", true);
      await getLanguageList();
      await fetchCountry();
      if (curMedicalRepresentativeId > 0) {
        const MedicalRepresentative =
          await getMedicalRepresentativeDetailById();
        if (MedicalRepresentative?.countryId) {
          await fetchCountryRegionData(MedicalRepresentative?.countryId);
        }
      } else {
        await fetchCountryRegionData(methods.getValues()?.countryId?.value);
        await getMedicalRepresentativeNumber();
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      console.log(e);
    }
  };

  // USEEFFECTS
  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, [curMedicalRepresentativeId]);

  // HANDLE DELAY FOR SKELETON
  useEffect(() => {
    if (!isLoading) {
      const timeout = setTimeout(() => setShowSkeleton(false), 50); // Delay unmounting the Skeleton
      return () => clearTimeout(timeout);
    } else {
      setShowSkeleton(true);
    }
  }, [isLoading]);

  return (
    <Box>
      <Box className="main-container" pl={0}>
        {!isForPopUp && (
          <Grid container className="page-header">
            <Grid item xs={6} sm={6} md={6}>
              <Box
                sx={{
                  display: "flex",
                  height: "100%",
                  alignItems: "center",
                }}
              >
                <CustomBreadcrumbs />
              </Box>
            </Grid>
            <Grid item xs={6} sm={6} md={6}>
              <Box className="page-header-icon">
                <Button
                  color="primary"
                  variant="outlined"
                  type="button"
                  className="secondary-button"
                  startIcon={<ArrowBackIcon />}
                  onClick={handleBack}
                >
                  Back
                </Button>
              </Box>
            </Grid>
          </Grid>
        )}
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)} action="">
            <Grid container gap={2}>
              <Grid item xs={12} className={"group-container"}>
                <Grid item container xs={12} spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h6"> Basic Information </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.medicalRepresentativeNumber
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Medical Representative ID *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          placeholder=""
                          autoCapitalize="none"
                          type="text"
                          fullWidth
                          disabled={true}
                          id="medicalRepresentativeNumber"
                          size="small"
                          {...methods.register("medicalRepresentativeNumber")}
                          error={
                            !!methods.formState.errors
                              .medicalRepresentativeNumber
                          }
                          className={`text-input ${
                            methods.formState.errors.medicalRepresentativeNumber
                              ? "error"
                              : ""
                          }`}
                        />
                      </SkeletonWrapper>
                    </FormControl>
                    <Typography className="validation-message">
                      {
                        methods.formState.errors.medicalRepresentativeNumber
                          ?.message
                      }
                    </Typography>
                  </Grid>
                  <Grid item md={9}>
                    {" "}
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.prefix
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Prefix
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="prefix"
                          render={({ field }) => (
                            <CustomSelect
                              {...field}
                              isNotCreatable={true}
                              id={"prefix"}
                              FieldName={"prefix"}
                              options={prefixOptions}
                              openMenuOnFocus
                              isClearable
                              isSearchable
                              getOptionValue={(option: any) => option.value}
                              getOptionLabel={(option: any) => option.label}
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={async (e) => {
                                if (
                                  methods
                                    ?.getValues()
                                    ?.prefix?.value?.toString() !==
                                  e?.value?.toString()
                                ) {
                                  methods.setValue(
                                    "prefix",
                                    e?.value ? e : null,
                                    {
                                      shouldValidate: true,
                                    }
                                  );
                                }
                              }}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.prefix?.message}
                      </Typography>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.firstName
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        First Name *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          placeholder=""
                          autoCapitalize="none"
                          type="text"
                          fullWidth
                          size="small"
                          {...methods.register("firstName", {
                            onChange: (e) => {
                              const input = e.target;
                              const selectionStart = input.selectionStart;
                              const selectionEnd = input.selectionEnd;

                              // CONVERT INPUT INTO UPPERCASE LATTERS
                              e.target.value = input.value
                                ? input.value.toUpperCase()
                                : "";

                              // Restore the cursor position
                              setTimeout(() => {
                                input.setSelectionRange(
                                  selectionStart,
                                  selectionEnd
                                );
                              });

                              methods.setValue("firstName", e.target.value, {
                                shouldValidate: true,
                              }); // UPDATE VALUE
                            },
                          })}
                          error={!!methods.formState.errors.firstName}
                          className={`text-input ${
                            methods.formState.errors.firstName ? "error" : ""
                          }`}
                        />
                      </SkeletonWrapper>
                    </FormControl>
                    <Typography className="validation-message">
                      {methods.formState.errors.firstName?.message}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.middleName
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Middle Name
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          placeholder=""
                          autoCapitalize="none"
                          type="text"
                          fullWidth
                          size="small"
                          {...methods.register("middleName", {
                            onChange: (e) => {
                              const input = e.target;
                              const selectionStart = input.selectionStart;
                              const selectionEnd = input.selectionEnd;

                              // CONVERT INPUT INTO UPPERCASE LATTERS
                              e.target.value = input.value
                                ? input.value.toUpperCase()
                                : "";

                              // Restore the cursor position
                              setTimeout(() => {
                                input.setSelectionRange(
                                  selectionStart,
                                  selectionEnd
                                );
                              });

                              methods.setValue("middleName", e.target.value, {
                                shouldValidate: true,
                              }); // UPDATE VALUE
                            },
                          })}
                          error={!!methods.formState.errors.middleName}
                          className={`text-input ${
                            methods.formState.errors.middleName ? "error" : ""
                          }`}
                        />
                      </SkeletonWrapper>
                    </FormControl>
                    <Typography className="validation-message">
                      {methods.formState.errors.middleName?.message}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.lastName
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Last Name *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          placeholder=""
                          autoCapitalize="none"
                          type="text"
                          fullWidth
                          size="small"
                          {...methods.register("lastName", {
                            onChange: (e) => {
                              const input = e.target;
                              const selectionStart = input.selectionStart;
                              const selectionEnd = input.selectionEnd;

                              // CONVERT INPUT INTO UPPERCASE LATTERS
                              e.target.value = input.value
                                ? input.value.toUpperCase()
                                : "";

                              // Restore the cursor position
                              setTimeout(() => {
                                input.setSelectionRange(
                                  selectionStart,
                                  selectionEnd
                                );
                              });

                              methods.setValue("lastName", e.target.value, {
                                shouldValidate: true,
                              }); // UPDATE VALUE
                            },
                          })}
                          error={!!methods.formState.errors.lastName}
                          className={`text-input ${
                            methods.formState.errors.lastName ? "error" : ""
                          }`}
                        />
                      </SkeletonWrapper>
                    </FormControl>
                    <Typography className="validation-message">
                      {methods.formState.errors.lastName?.message}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.gender
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Gender *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="gender"
                          render={({ field }) => (
                            <CustomSelect
                              {...field}
                              isNotCreatable={true}
                              id={"gender"}
                              FieldName={"gender"}
                              options={genderOptions}
                              openMenuOnFocus
                              isClearable
                              isSearchable
                              getOptionValue={(option: any) => option.value}
                              getOptionLabel={(option: any) => option.label}
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={async (e) => {
                                if (
                                  methods
                                    ?.getValues()
                                    ?.gender?.value?.toString() !==
                                  e?.value?.toString()
                                ) {
                                  methods.setValue(
                                    "gender",
                                    e?.value ? e : null,
                                    {
                                      shouldValidate: true,
                                    }
                                  );
                                }
                              }}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.gender?.message}
                      </Typography>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <div className="input-group">
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.primaryMobileNumber
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Primary Mobile Number (whatsapp){" "}
                        <CustomTooltip
                          title="This field is important to be filled for better communication"
                          hideHeader={true}
                        ></CustomTooltip>
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <PhoneInput
                          id="primaryMobileNumber"
                          international
                          countryCallingCodeEditable={false}
                          defaultCountry="IN"
                          {...methods.register("primaryMobileNumber")}
                          error={
                            methods.formState.errors.primaryMobileNumber
                              ?.message
                          }
                          className={`phone-input ${
                            methods.formState.errors.primaryMobileNumber
                              ? "error"
                              : ""
                          }`}
                          onChange={(e) => {
                            methods.setValue("primaryMobileNumber", e || "", {
                              shouldValidate: true,
                            });
                          }}
                          value={methods.getValues()?.primaryMobileNumber || ""}
                          placeholder="Enter phone number"
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.primaryMobileNumber?.message}
                      </Typography>
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.dateOfBirth
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Date of Birth
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <DatePickerComponent
                          format="DD-MM-YYYY"
                          disabled={false}
                          mode="date"
                          slotProps={{ textField: { size: "small" } }}
                          value={
                            methods?.getValues()?.dateOfBirth
                              ? dayjs(methods?.getValues()?.dateOfBirth)
                              : null
                          }
                          onChange={(e: any) => {
                            if (e !== null && isValid(e.$d)) {
                              let savedDateFormate: any = format(
                                e.$d,
                                "yyyy-MM-dd"
                              );
                              methods.setValue("dateOfBirth", savedDateFormate);
                            } else {
                              methods.setValue("dateOfBirth", "");
                            }
                          }}
                        />
                      </SkeletonWrapper>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.preferredLanguage
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Preferred Language
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="preferredLanguage"
                          render={({ field }) => (
                            <CustomSelect
                              {...field}
                              isNotCreatable={true}
                              id={"preferredLanguage"}
                              FieldName={"preferredLanguage"}
                              options={languageData}
                              openMenuOnFocus
                              isClearable
                              isSearchable
                              getOptionValue={(option: any) => option.value}
                              getOptionLabel={(option: any) => option.label}
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={async (e) => {
                                if (
                                  methods
                                    ?.getValues()
                                    ?.preferredLanguage?.value?.toString() !==
                                  e?.value?.toString()
                                ) {
                                  methods.setValue(
                                    "preferredLanguage",
                                    e?.value ? e : null,
                                    {
                                      shouldValidate: true,
                                    }
                                  );
                                }
                              }}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.preferredLanguage?.message}
                      </Typography>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.maritalStatus
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Marital Status
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="maritalStatus"
                          render={({ field }) => (
                            <CustomSelect
                              {...field}
                              isNotCreatable={true}
                              id={"maritalStatus"}
                              FieldName={"maritalStatus"}
                              options={maritalStatusOptions}
                              openMenuOnFocus
                              isClearable
                              isSearchable
                              getOptionValue={(option: any) => option.value}
                              getOptionLabel={(option: any) => option.label}
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={async (e) => {
                                if (
                                  methods
                                    ?.getValues()
                                    ?.maritalStatus?.value?.toString() !==
                                  e?.value?.toString()
                                ) {
                                  methods.setValue(
                                    "maritalStatus",
                                    e?.value ? e : null,
                                    {
                                      shouldValidate: true,
                                    }
                                  );
                                }
                              }}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.maritalStatus?.message}
                      </Typography>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} className={"group-container"}>
                <Grid item container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h6"> Company Information </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.medicineCompanyId
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Company Name{" "}
                        <CustomTooltip
                          title="This field is important to be filled for better communication"
                          hideHeader={true}
                        ></CustomTooltip>
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="medicineCompanyId"
                          render={({ field }) => (
                            <CustomAsyncSelect
                              {...field}
                              isNotCreatable={false}
                              id={"medicineCompanyId"}
                              FieldName={"medicineCompanyId"}
                              defaultOptions={medicineCompanyData}
                              loadOptions={loadMedicineCompanyList}
                              isClearable
                              openMenuOnFocus
                              isSearchable
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={async (e) => {
                                if (e) {
                                  if (e.__isNew__ === true) {
                                    handleClickOpen(true, e.label);
                                  }
                                }
                                if (
                                  methods
                                    ?.getValues()
                                    ?.medicineCompanyId?.value?.toString() !==
                                  e?.value?.toString()
                                ) {
                                  methods.setValue(
                                    "medicineCompanyId",
                                    e?.value ? e : null,
                                    {
                                      shouldValidate: true,
                                    }
                                  );
                                }
                              }}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.medicineCompanyId?.message}
                      </Typography>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} className={"group-container"}>
                <Typography
                  className="font-size-16 text-link"
                  onClick={(e) => {
                    setSeeMore(!seeMore);
                  }}
                >
                  {seeMore ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Typography>See Less</Typography>
                      <ExpandLess />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Typography>See More</Typography>
                      <ExpandMore />
                    </Box>
                  )}
                </Typography>
              </Grid>
              {seeMore && (
                <Grid item xs={12} className={"group-container"}>
                  <Grid item container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="h6">
                        {" "}
                        Contact Information{" "}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <FormLabel
                          className={`fileds-label-text  ${
                            methods.formState.errors.address1
                              ? "validation-message"
                              : ""
                          }`}
                        >
                          Address 1{" "}
                          <CustomTooltip
                            title="This field is important to be filled for better communication"
                            hideHeader={true}
                          ></CustomTooltip>
                        </FormLabel>
                        <SkeletonWrapper
                          isLoading={showSkeleton}
                          className="skeleton-input"
                        >
                          <CustomTextField
                            placeholder=""
                            autoCapitalize="none"
                            type="text"
                            fullWidth
                            size="small"
                            {...methods.register("address1")}
                            error={!!methods.formState.errors.address1}
                            className={`text-input ${
                              methods.formState.errors.address1 ? "error" : ""
                            }`}
                          />
                        </SkeletonWrapper>
                      </FormControl>
                      <Typography className="validation-message">
                        {methods.formState.errors.address1?.message}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <FormLabel
                          className={`fileds-label-text  ${
                            methods.formState.errors.address2
                              ? "validation-message"
                              : ""
                          }`}
                        >
                          Address 2{" "}
                          <CustomTooltip
                            title="This field is important to be filled for better communication"
                            hideHeader={true}
                          ></CustomTooltip>
                        </FormLabel>
                        <SkeletonWrapper
                          isLoading={showSkeleton}
                          className="skeleton-input"
                        >
                          <CustomTextField
                            placeholder=""
                            autoCapitalize="none"
                            type="text"
                            fullWidth
                            size="small"
                            {...methods.register("address2")}
                            error={!!methods.formState.errors.address2}
                            className={`text-input ${
                              methods.formState.errors.address2 ? "error" : ""
                            }`}
                          />
                        </SkeletonWrapper>
                      </FormControl>
                      <Typography className="validation-message">
                        {methods.formState.errors.address2?.message}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth>
                        <FormLabel
                          className={`fileds-label-text  ${
                            methods.formState.errors.city
                              ? "validation-message"
                              : ""
                          }`}
                        >
                          City{" "}
                          <CustomTooltip
                            title="This field is important to be filled for better communication"
                            hideHeader={true}
                          ></CustomTooltip>
                        </FormLabel>
                        <SkeletonWrapper
                          isLoading={showSkeleton}
                          className="skeleton-input"
                        >
                          <CustomTextField
                            placeholder=""
                            autoCapitalize="none"
                            type="text"
                            fullWidth
                            size="small"
                            {...methods.register("city")}
                            error={!!methods.formState.errors.city}
                            className={`text-input ${
                              methods.formState.errors.city ? "error" : ""
                            }`}
                          />
                        </SkeletonWrapper>
                      </FormControl>
                      <Typography className="validation-message">
                        {methods.formState.errors.city?.message}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth>
                        <FormLabel
                          className={`fileds-label-text  ${
                            methods.formState.errors.postalCode
                              ? "validation-message"
                              : ""
                          }`}
                        >
                          Postal Code
                        </FormLabel>
                        <SkeletonWrapper
                          isLoading={showSkeleton}
                          className="skeleton-input"
                        >
                          <CustomTextField
                            placeholder=""
                            autoCapitalize="none"
                            type="text"
                            fullWidth
                            id="postalCode"
                            size="small"
                            {...methods.register("postalCode")}
                            error={!!methods.formState.errors.postalCode}
                            className={`text-input ${
                              methods.formState.errors.postalCode ? "error" : ""
                            }`}
                          />
                        </SkeletonWrapper>
                      </FormControl>
                      <Typography className="validation-message">
                        {methods.formState.errors.postalCode?.message}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth>
                        <FormLabel
                          className={`fileds-label-text  ${
                            methods.formState.errors.countryId
                              ? "validation-message"
                              : ""
                          }`}
                        >
                          Country
                        </FormLabel>
                        <SkeletonWrapper
                          isLoading={showSkeleton}
                          className="skeleton-input"
                        >
                          <Controller
                            control={methods.control}
                            name="countryId"
                            render={({ field }) => (
                              <CustomSelect
                                {...field}
                                isNotCreatable={true}
                                id={"countryId"}
                                FieldName={"countryId"}
                                options={countryData}
                                openMenuOnFocus
                                isClearable
                                isSearchable
                                menuPortalTarget={document.querySelector(
                                  "body"
                                )}
                                handleChange={async (e) => {
                                  if (
                                    methods
                                      ?.getValues()
                                      ?.countryId?.value?.toString() !==
                                    e?.value?.toString()
                                  ) {
                                    methods.setValue(
                                      "countryId",
                                      e?.value ? e : null,
                                      {
                                        shouldValidate: true,
                                      }
                                    );
                                    methods.setValue(
                                      "countryName",
                                      e?.value ? e?.label : null,
                                      {
                                        shouldValidate: true,
                                      }
                                    );
                                    methods.setValue("countryRegionId", null, {
                                      shouldValidate: true,
                                    });
                                    methods.setValue("countryRegionName", "", {
                                      shouldValidate: true,
                                    });
                                    if (e?.value !== null) {
                                      setIsLoading(true);
                                      await fetchCountryRegionData(e?.value);
                                      setIsLoading(false);
                                    } else {
                                      setCountryRegionData([]);
                                    }
                                  }
                                }}
                                className={`text-input ${
                                  methods.formState.errors.postalCode
                                    ? "error"
                                    : ""
                                }`}
                              />
                            )}
                          />
                        </SkeletonWrapper>
                        <Typography className="validation-message">
                          {methods.formState.errors.countryId?.message}
                        </Typography>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth>
                        <FormLabel
                          className={`fileds-label-text  ${
                            methods.formState.errors.countryRegionId
                              ? "validation-message"
                              : ""
                          }`}
                        >
                          State
                        </FormLabel>
                        <SkeletonWrapper
                          isLoading={showSkeleton}
                          className="skeleton-input"
                        >
                          <Controller
                            control={methods.control}
                            name="countryRegionId"
                            render={({ field }) => (
                              <CustomSelect
                                {...field}
                                isNotCreatable={true}
                                id={"countryRegionId"}
                                FieldName={"countryRegionId"}
                                options={countryRegionData}
                                openMenuOnFocus
                                isClearable
                                isSearchable
                                menuPortalTarget={document.querySelector(
                                  "body"
                                )}
                                handleChange={(e) => {
                                  methods.setValue(
                                    "countryRegionId",
                                    e?.value ? e : null,
                                    {
                                      shouldValidate: true,
                                    }
                                  );
                                  methods.setValue(
                                    "countryRegionName",
                                    e?.value ? e?.label : null,
                                    {
                                      shouldValidate: true,
                                    }
                                  );
                                }}
                                className={`text-input ${
                                  methods.formState.errors.countryId
                                    ? "error"
                                    : ""
                                }`}
                              />
                            )}
                          />
                        </SkeletonWrapper>
                        <Typography className="validation-message">
                          {methods.formState.errors.countryRegionId?.message}
                        </Typography>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth>
                        <FormLabel
                          className={`fileds-label-text  ${
                            methods.formState.errors.email
                              ? "validation-message"
                              : ""
                          }`}
                        >
                          Email{" "}
                          <CustomTooltip
                            title="This field is important to be filled for better communication"
                            hideHeader={true}
                          ></CustomTooltip>
                        </FormLabel>
                        <SkeletonWrapper
                          isLoading={showSkeleton}
                          className="skeleton-input"
                        >
                          <CustomTextField
                            placeholder=""
                            autoCapitalize="none"
                            type="text"
                            fullWidth
                            size="small"
                            {...methods.register("email")}
                            error={!!methods.formState.errors.email}
                            className={`text-input ${
                              methods.formState.errors.email ? "error" : ""
                            }`}
                          />
                        </SkeletonWrapper>
                      </FormControl>
                      <Typography className="validation-message">
                        {methods.formState.errors.email?.message}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <div className="input-group">
                        <FormLabel
                          className={`fileds-label-text  ${
                            methods.formState.errors.secondaryMobileNumber
                              ? "validation-message"
                              : ""
                          }`}
                        >
                          Secondary Mobile Number{" "}
                          <CustomTooltip
                            title="This field is important to be filled for better communication"
                            hideHeader={true}
                          ></CustomTooltip>
                        </FormLabel>
                        <SkeletonWrapper
                          isLoading={showSkeleton}
                          className="skeleton-input"
                        >
                          <PhoneInput
                            id="secondaryMobileNumber"
                            international
                            countryCallingCodeEditable={false}
                            defaultCountry="IN"
                            {...methods.register("secondaryMobileNumber")}
                            error={
                              methods.formState.errors.secondaryMobileNumber
                                ?.message
                            }
                            className={`phone-input ${
                              methods.formState.errors.secondaryMobileNumber
                                ? "error"
                                : ""
                            }`}
                            value={
                              methods.getValues()?.secondaryMobileNumber || ""
                            }
                            onChange={(e) => {
                              methods.setValue(
                                "secondaryMobileNumber",
                                e || ""
                              );
                            }}
                            placeholder="Enter phone number"
                          />
                        </SkeletonWrapper>
                        <Typography className="validation-message">
                          {
                            methods.formState.errors.secondaryMobileNumber
                              ?.message
                          }
                        </Typography>
                      </div>
                    </Grid>
                  </Grid>
                </Grid>
              )}
              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "end", gap: 2 }}>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    startIcon={<> </>}
                    variant="contained"
                    type="submit"
                    className="primary-button"
                    name="save"
                    disabled={showSkeleton || isLoading}
                  >
                    {curMedicalRepresentativeId > 0 ? "Update" : "Save"}
                  </LoadingButton>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    startIcon={<> </>}
                    variant="outlined"
                    type="button"
                    className="secondary-button"
                    name="cancel"
                    onClick={handleBack}
                  >
                    Cancel
                  </LoadingButton>
                </Box>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
        <Dialog
          className="dialogPage"
          open={customPopUp.open}
          onClose={(e, reason) => {
            if (reason !== "backdropClick") {
              handleClose();
            }
          }}
          fullScreen
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          PaperProps={{
            style: {
              width: "1200px",
              height: "400px",
              maxWidth: "none",
            },
          }}
        >
          <AppBar sx={{ position: "relative" }}>
            <Toolbar>
              <Typography
                style={{ fontWeight: 600, marginLeft: 0 }}
                sx={{ ml: 2, flex: 1 }}
                variant="h6"
              >
                Save Medicine Company
              </Typography>
            </Toolbar>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </AppBar>
          <DialogContent sx={{ padding: "12px 24px" }}>
            <MedicineCompanySave
              handleClosePopUp={(code, company) => {
                handleManagePopup(code, company);
              }}
              isForPopUp={customPopUp.open}
              name={customPopUp.name}
            />
          </DialogContent>
          <Divider />
        </Dialog>
      </Box>
    </Box>
  );
}
